'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import BagIcon from '@/fo/icons/base/icons/bag.svg';
import CatalogIcon from '@/fo/icons/base/icons/catalog_app.svg';
import FavoriteIcon from '@/fo/icons/base/icons/heart.svg';
import HomeIcon from '@/fo/icons/base/icons/home.svg';
import UserIcon from '@/fo/icons/base/icons/login.svg';
import {
  storeProduct,
  selectCounterCart,
  selectCounterWishlist
} from '@/fo/store/product';
import { UiAppMenuMobile, UiAppMenuMobileProps } from '@/fo/ui/app/menu/mobile';
import { UiKitCounterBase } from '@/fo/ui/kit/counter/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';

import { TAPBAR_MOBILE_ID } from '../constants';

import { styles } from './UiAppTapbarMobile.styles';

export interface UiAppTapbarMobileProps {
  isAuthorized?: boolean;
  children?: React.ReactNode;
  menuProps?: Omit<UiAppMenuMobileProps, 'className'>;
  customVariables?: {
    [key: string]: string | unknown[];
  };
  searchSuggestion?: React.ReactNode;
}

/** Мобильный, навигационный бар, для десктопа скрыт на уровне стилей */
export function UiAppTapbarMobile({
  isAuthorized = false,
  children,
  menuProps,
  customVariables,
  searchSuggestion
}: UiAppTapbarMobileProps) {
  const slots = styles();
  const isMobile = useMediaQuery('md', 'max');
  const router = useRouter();
  const [showMenu, setShowMenu] = useState(false);
  const pathname = usePathname();

  const cartCount = useSelector(storeProduct, selectCounterCart);
  const wishlistCount = useSelector(storeProduct, selectCounterWishlist);

  useEffect(() => {
    setShowMenu(false);
  }, [pathname, isMobile]);

  useEffect(() => {
    if (showMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu]);

  if (!isMobile) {
    return (
      <div
        className={slots.base()}
        id={TAPBAR_MOBILE_ID}
      />
    );
  }

  const handleProfileClick = () => {
    if (isAuthorized) {
      router.push('/account');
    } else {
      utilEventAuthModal.open('header-mobile');
    }
  };

  const handleFavoriteClick = () => {
    if (isAuthorized) {
      router.push('/account/wishlist');
    } else {
      utilEventAuthModal.open('header-mobile');
    }
  };

  return (
    <>
      <div
        className={slots.base()}
        id={TAPBAR_MOBILE_ID}
      >
        <div className={slots.container()}>
          <Link
            href="/"
            className={slots.item()}
            onClick={() => setShowMenu(false)}
          >
            <HomeIcon />
          </Link>
          <div
            className={slots.item()}
            data-selected={showMenu}
            onClick={() => setShowMenu((s) => !s)}
          >
            <CatalogIcon />
          </div>
          <div
            className={slots.item()}
            onClick={handleFavoriteClick}
          >
            <div className={slots.icon()}>
              <FavoriteIcon />
              <UiKitCounterBase
                color="primary"
                className={slots.linkCounter()}
                counter={wishlistCount}
              />
            </div>
          </div>
          <div
            className={slots.item()}
            onClick={handleProfileClick}
          >
            <UserIcon />
          </div>
          <div
            onClick={() => utilEventCartModal.open()}
            className={slots.item()}
          >
            <div className={slots.icon()}>
              <BagIcon />
              <UiKitCounterBase
                color="primary"
                className={slots.linkCounter()}
                counter={cartCount}
              />
            </div>
          </div>
          {showMenu && (
            <UiAppMenuMobile
              {...menuProps}
              className={slots.menu()}
              closeMenu={() => setShowMenu(false)}
              customVariables={customVariables}
              searchSuggestion={searchSuggestion}
            />
          )}
        </div>
      </div>
      {children}
    </>
  );
}
